/**
 * @module  public/js/services/featureflagservice.js
 * @class   FeatureFlagService
 * @summary Persist-per-user feature flags with reactive getters/setters backed by Firestore.
 *
 * @description
 * Handles feature flag storage and retrieval for each user via Firebase Firestore.
 * Reads authoritative server state after auth/tenant hydration, scopes cached
 * values to the tenant and user, and emits realtime server updates.
 *
 * ## Key Features
 * – Stores flags under tenants/{tenantId}/Users/{uid}/flags/{flagId}.
 * – Local cache is isolated by tenant and user.
 * – Initial and realtime decisions ignore stale Firestore cache snapshots.
 * – `subscribe()` streams realtime changes to interested listeners.
 * – Fallback defaults to false for unset flags (opt-in convention).
 *
 * ## Public API
 * – `isEnabled(flagId)` — resolve current enabled state (default false).
 * – `set(flagId, enabled)` — create or update a flag’s value.
 * – `subscribe(flagId, cb)` — register a listener for live changes.
 * – `isConnected()` — convenience boolean wrapper for `getConnectedProvider()`.
 *
 * ## Dependencies
 * { AuthService }
 *
 * @author   Auto-Generated
 * @version  1.0.0
 * @since    2025-04-28
 */

import {
  getDocFromServer,
  onSnapshot,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';
import { docWithTenant, writeWithTenant } from '../utils/firestoreHelpers.js';
import endpoints from '../core/endpoints-service.js';

export default class FeatureFlagService {
  #auth;

  /** {Map<string,boolean>} tenant/user-scoped local flag cache */
  #cache = new Map();

  /** {Map<string,string>} flag → active tenant/user cache key */
  #activeCacheKeys = new Map();

  /** {Map<string,Set<Function>>} flag → subscriber callbacks */
  #subs = new Map();

  /** {Map<string,Function>} tenant/user flag key → Firestore unsubscribe */
  #listeners = new Map();

  constructor({ authService }) {
    this.#auth = authService; // ✅ matches the DI container
  }

  /** ------------------------------------------------------------------
   * Build a Firestore document reference for a specific flag.
   * @param {string} flagId
   */
  #flagPath(uid, flagId) {
    return `Users/${uid}/flags/${flagId}`;
  }

  /**
   * Resolve a signed-in user together with an authenticated tenant claim.
   * The ready user is preferred when the current Auth object is still being
   * decorated during an ID-token refresh.
   *
   * @returns {Promise<{
   *   user: import('firebase/auth').User,
   *   uid: string,
   *   tenantId: string,
   * }>} Tenant-safe user context.
   */
  async #resolveUserContext() {
    const readyUser = await this.#auth.waitForLogin();
    const currentUser = this.#auth.currentUser;
    const sameUser =
      currentUser?.uid && currentUser.uid === readyUser?.uid
        ? currentUser
        : null;
    const currentUserChanged =
      currentUser?.uid && currentUser.uid !== readyUser?.uid;
    const user = currentUserChanged
      ? currentUser
      : (sameUser?.tenantId ? sameUser : null) || readyUser || currentUser;
    const uid = String(user?.uid || '').trim();
    let tenantId = String(
      user?.tenantId ||
        (sameUser?.uid === readyUser?.uid && readyUser?.tenantId) ||
        ''
    ).trim();

    if (!tenantId && typeof user?.getIdTokenResult === 'function') {
      const tokenResult = await user.getIdTokenResult();
      tenantId = String(tokenResult?.claims?.tenantId || '').trim();
    }

    if (!uid || !tenantId) {
      throw new Error(
        'Unable to resolve calling permissions for this session.'
      );
    }

    try {
      user.tenantId = tenantId;
      if (sameUser) sameUser.tenantId = tenantId;
    } catch {
      /* Firebase user objects may be non-extensible in test or browser shims. */
    }

    return { user, uid, tenantId };
  }

  /**
   * Build a cache key that cannot leak flag state across tenants or users.
   *
   * @param {string} tenantId - Authenticated tenant identifier.
   * @param {string} uid - Firebase user identifier.
   * @param {string} flagId - Feature flag identifier.
   * @returns {string} Stable tenant/user-scoped cache key.
   */
  #cacheKey(tenantId, uid, flagId) {
    return JSON.stringify([tenantId, uid, flagId]);
  }

  /** ------------------------------------------------------------------
   * isEnabled
   * @param   {string}  flagId
   * @returns {Promise<boolean>}
   */
  async isEnabled(flagId) {
    const { uid, tenantId } = await this.#resolveUserContext();
    const cacheKey = this.#cacheKey(tenantId, uid, flagId);
    this.#activeCacheKeys.set(flagId, cacheKey);

    if (this.#cache.has(cacheKey)) {
      this.#initListener({ flagId, uid, tenantId, cacheKey });
      return this.#cache.get(cacheKey);
    }

    const path = this.#flagPath(uid, flagId);
    const ref = docWithTenant(tenantId, path);
    const snap = await getDocFromServer(ref);
    const doc = snap.exists() ? snap.data() : null;

    let enabled = doc ? !!doc.enabled : false;

    if (!doc && flagId === 'twilioVoice' && endpoints.env === 'development') {
      await writeWithTenant(
        tenantId,
        ref,
        { enabled: true },
        { merge: true }
      ).catch(() => {});
      enabled = true;
    }

    this.#cache.set(cacheKey, enabled);
    this.#initListener({ flagId, uid, tenantId, cacheKey });
    return enabled;
  }

  /** ------------------------------------------------------------------
   * set – Enable / disable a flag.
   * @param {string}  flagId
   * @param {boolean} enabled
   * @returns {Promise<void>}
   */
  async set(flagId, enabled) {
    const { uid, tenantId } = await this.#resolveUserContext();
    const cacheKey = this.#cacheKey(tenantId, uid, flagId);
    const ref = docWithTenant(tenantId, this.#flagPath(uid, flagId));
    await writeWithTenant(
      tenantId,
      ref,
      { enabled: !!enabled },
      { merge: true }
    );
    this.#activeCacheKeys.set(flagId, cacheKey);
    this.#updateCache(flagId, cacheKey, !!enabled);
    this.#initListener({ flagId, uid, tenantId, cacheKey });
  }

  /** ------------------------------------------------------------------
   * subscribe – live updates for a single flag.
   * @param {string}   flagId
   * @param {Function} callback (enabled:boolean)=>void
   * @returns {Function} unsubscribe
   */
  subscribe(flagId, callback) {
    if (!this.#subs.has(flagId)) this.#subs.set(flagId, new Set());
    const set = this.#subs.get(flagId);
    set.add(callback);

    // push current known value immediately
    const activeCacheKey = this.#activeCacheKeys.get(flagId);
    if (activeCacheKey && this.#cache.has(activeCacheKey)) {
      callback(this.#cache.get(activeCacheKey));
    } else {
      // ensure fetch + listener kick off
      this.isEnabled(flagId)
        .then(() => {
          const currentCacheKey = this.#activeCacheKeys.get(flagId);
          if (
            set.has(callback) &&
            currentCacheKey &&
            this.#cache.has(currentCacheKey)
          ) {
            callback(this.#cache.get(currentCacheKey));
          }
        })
        .catch(() => {});
    }

    return () => set.delete(callback);
  }

  // ---------- private helpers ---------------------------------------

  #updateCache(flagId, cacheKey, value) {
    const prev = this.#cache.get(cacheKey);
    if (prev === value) return; // no change

    this.#cache.set(cacheKey, value);
    if (this.#activeCacheKeys.get(flagId) !== cacheKey) return;
    if (this.#subs.has(flagId)) {
      this.#subs.get(flagId).forEach((cb) => cb(value));
    }
  }

  /**
   * Start a realtime listener for one tenant/user-scoped flag context.
   *
   * @param {{flagId:string, uid:string, tenantId:string, cacheKey:string}} context
   *   Resolved feature flag context.
   * @returns {void}
   */
  #initListener({ flagId, uid, tenantId, cacheKey }) {
    if (this.#listeners.has(cacheKey)) return;

    const ref = docWithTenant(tenantId, this.#flagPath(uid, flagId));
    const unsub = onSnapshot(
      ref,
      { includeMetadataChanges: true },
      (snap) => {
        if (snap.metadata?.fromCache === true) return;
        const enabled = snap.exists() ? !!snap.data().enabled : false;
        this.#updateCache(flagId, cacheKey, enabled);
      },
      (err) => {
        console.warn('[FeatureFlagService] listener unavailable', {
          flagId,
          message: err?.message || String(err),
        });
      }
    );
    this.#listeners.set(cacheKey, unsub);
  }
}
